import { useCallback, useEffect } from "react";
import type { GalleryItem } from "@/types";
import { galleryCategoryLabels } from "@/lib/galleryMeta";

interface LightboxProps {
  items: GalleryItem[];
  activeIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function Lightbox({ items, activeIndex, onClose, onNavigate }: LightboxProps) {
  const item = items[activeIndex];

  const goPrev = useCallback(() => {
    onNavigate((activeIndex - 1 + items.length) % items.length);
  }, [activeIndex, items.length, onNavigate]);

  const goNext = useCallback(() => {
    onNavigate((activeIndex + 1) % items.length);
  }, [activeIndex, items.length, onNavigate]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [goNext, goPrev, onClose]);

  if (!item) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Visor de imagen: ${item.titulo}`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-primary-950/92 p-4 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar visor"
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary-50/10 text-primary-50 transition-colors duration-200 hover:bg-primary-50/20 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 md:right-6 md:top-6"
      >
        <i className="ri-close-line text-2xl leading-none" aria-hidden="true"></i>
      </button>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goPrev();
            }}
            aria-label="Imagen anterior"
            className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-primary-50/10 text-primary-50 transition-colors duration-200 hover:bg-primary-50/20 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 md:left-6"
          >
            <i className="ri-arrow-left-s-line text-2xl leading-none" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goNext();
            }}
            aria-label="Imagen siguiente"
            className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-primary-50/10 text-primary-50 transition-colors duration-200 hover:bg-primary-50/20 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 md:right-6"
          >
            <i className="ri-arrow-right-s-line text-2xl leading-none" aria-hidden="true"></i>
          </button>
        </>
      )}

      <figure
        className="flex w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-background-50"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex max-h-[70vh] w-full items-center justify-center bg-primary-950">
          <img
            src={item.imagen_url}
            alt={`${item.titulo} — trabajo realizado por AXONIA GROUP`}
            title={`${item.titulo} — ${galleryCategoryLabels[item.categoria]}`}
            className="max-h-[70vh] w-full object-contain"
          />
        </div>
        <figcaption className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="text-[11px] font-label font-semibold uppercase tracking-wider text-accent-700">
              {galleryCategoryLabels[item.categoria]}
            </span>
            <h3 className="mt-1 text-lg font-heading font-semibold text-foreground-950">
              {item.titulo}
            </h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              {item.descripcion}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-1 text-xs text-foreground-500 sm:items-end">
            {item.fecha && (
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm leading-none" aria-hidden="true"></i>
                {new Date(item.fecha).toLocaleDateString("es-PE", {
                  year: "numeric",
                  month: "long",
                })}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-image-2-line text-sm leading-none" aria-hidden="true"></i>
              {activeIndex + 1} de {items.length}
            </span>
          </div>
        </figcaption>
      </figure>
    </div>
  );
}